import type { UnifiedProductSummary } from '@noths/polaris-client-unified-product-service';
import { fetchUnifiedProductsByCodes } from '@noths/polaris-client-unified-product-service';
import getConfig from 'next/config';

import { UNIFIED_PRODUCTS_CACHE_TTL_SECONDS } from 'src/constants/unifiedProductCache';
import { withRedisFetchCache } from 'src/services/withRedisFetchCache';
import type { NextConfig } from 'src/types/NextConfig';

const { serverRuntimeConfig } = getConfig() as NextConfig;
const { UNIFIED_PRODUCT_SERVICE_URL } = serverRuntimeConfig;

/**
 * Higher than the 800ms the batched cat-service-api call this replaces used. UPS answers a warm
 * batch of twelve in well under 100ms, but its first request after an idle spell measured about
 * 1.1s, and a timeout here does not degrade the carousel, it removes the section from the page
 * through `preventRender`. A slow render once a minute per product set, which is all the cache
 * lets through, is the better failure.
 */
const CLIENT_TIMEOUT = 1500;

export interface UnifiedProductsResult {
  /** Requested codes the service returned nothing for, in the order they were requested. */
  missingCodes: number[];
  products: UnifiedProductSummary[];
}

/**
 * Sorted and deduplicated so two sections asking for the same products share one cache entry
 * regardless of the order the codes appear in Contentstack. The normalised list is also what gets
 * sent upstream, so the cache key and the request it stands for cannot drift apart. This order
 * serves the cache and the upstream request only, never the caller: the caller's own order is put
 * back on the way out.
 */
const normalizeProductCodes = (productCodes: number[]) =>
  [...new Set(productCodes)].sort((a, b) => a - b);

/**
 * Contentstack order is merchandising intent, so a carousel renders the returned array positionally
 * and needs it back in the order it asked for rather than the normalised order sent upstream.
 *
 * Builds a new array: the array this receives is the one held in the cache and shared with every
 * caller coalesced onto the same in-flight request, so sorting it in place would reorder their
 * results too. `missingCodes` is derived here rather than cached because it is a pure function of
 * the products and the caller's own codes, and the cached copy would be in the wrong order anyway.
 */
const orderAsRequested = (
  products: UnifiedProductSummary[],
  productCodes: number[],
): UnifiedProductsResult => {
  const productsByCode = new Map(products.map((product) => [product.code, product]));
  const requestedCodes = [...new Set(productCodes)];

  return {
    missingCodes: requestedCodes.filter((code) => !productsByCode.has(code)),
    products: requestedCodes
      .map((code) => productsByCode.get(code))
      .filter((product): product is UnifiedProductSummary => !!product),
  };
};

/**
 * Lets the client's throw through rather than catching it, which is what lets
 * `withRedisFetchCache` tell success from failure: it serves the previous response when there is
 * one and falls back to null when there is not. Returning null here would cache the failure as if
 * it were an empty catalog and blank every affected carousel for the whole TTL.
 */
const fetchFromUnifiedProductService = async (productCodes: number[]) => {
  const { products } = await fetchUnifiedProductsByCodes({
    endpoint: UNIFIED_PRODUCT_SERVICE_URL,
    productCodes,
    timeout: CLIENT_TIMEOUT,
  });

  return products;
};

export const fetchUnifiedProducts = async (
  productCodes: number[],
): Promise<UnifiedProductsResult | null> => {
  const normalizedProductCodes = normalizeProductCodes(productCodes);

  if (normalizedProductCodes.length === 0) {
    return { missingCodes: [], products: [] };
  }

  const products = await withRedisFetchCache<UnifiedProductSummary[] | null>({
    allowCaching: true,
    cacheKey: `unified-products-${normalizedProductCodes.join(',')}`,
    cacheTTLInSeconds: UNIFIED_PRODUCTS_CACHE_TTL_SECONDS,
    fallbackOnError: null,
    fetcher: () => fetchFromUnifiedProductService(normalizedProductCodes),
  });

  // null is a failed request, which callers surface as a preventRender error.
  return products && orderAsRequested(products, productCodes);
};
